import { BodyRegular, BodySmall, FlexCol, FlexColSm } from '../Elements';
import styled from 'styled-components';
import { sizes, theme } from '../../theme';
import React from 'react';

export type RadioFieldOption = {
    value: number | string;
    title: string;
    description?: string;
    aria: string;
    inputProps: object;
    disabled?: boolean;
};

type RadioFieldsProps = {
    options: RadioFieldOption[];
    onBlur?: Function;
    autoFocus?: boolean;
};

const RadioFields = React.forwardRef<HTMLInputElement, RadioFieldsProps>(({ options, onBlur, autoFocus }, ref) => {
    return (
        <FlexColSm onBlur={!!onBlur ? () => onBlur() : undefined}>
            {options.map((option, index) => {
                const fieldId = `activity-option-${option.value}`;

                return (
                    <OptionContainer key={option.value}>
                        <RadioInput
                            {...option.inputProps}
                            type="radio"
                            id={fieldId}
                            value={option.value}
                            $subHeading={!!option.description}
                            disabled={option.disabled ?? false}
                            autoFocus={!!autoFocus && index === 0}
                        />
                        <TextBlock htmlFor={fieldId} aria-label={option.aria}>
                            <FlexCol>
                                <BodyRegular $bold>{option.title}</BodyRegular>
                                {option.description && <Description>{option.description}</Description>}
                            </FlexCol>
                        </TextBlock>
                    </OptionContainer>
                );
            })}
        </FlexColSm>
    );
});

export default RadioFields;

const OptionContainer = styled.div<{ $maxWidth?: string }>`
    position: relative;
    width: 100%;
    align-items: center;
`;

const TextBlock = styled.label`
    display: flex;
    gap: ${sizes.spacingXs};
    width: 100%;
    border: solid 1px ${theme.cardBorder};
    border-radius: ${sizes.borderRadiusLg};
    padding: ${sizes.rem.sm} ${sizes.rem.sm} ${sizes.rem.sm} 3.5rem;
`;

const Description = styled(BodySmall)`
    color: ${theme.textSecondaryColour};
`;

const RadioInput = styled.input<{ $subHeading?: boolean }>`
    position: absolute;
    left: 0;
    // When there is a subheading, we want it inline with the top line only
    top: ${({ $subHeading }) => {
        return $subHeading ? sizes.rem.rg : '50%';
    }};
    transform: ${({ $subHeading }) => {
        return $subHeading ? null : 'translateY(-50%)';
    }};
    margin-left: ${sizes.spacingMd};
    width: 1.75rem;
    height: 1.75rem;
    &:focus ~ ${TextBlock} {
        background-color: ${theme.inputRadioButton.focusBackgroundColour};
        border: 2px solid ${theme.inputRadioButton.checkedBorderColour};
    }
    &:checked {
        accent-color: ${theme.inputRadioButton.activeRadioColour};
    }
    &:checked ~ ${TextBlock} {
        border: 2px solid ${theme.inputRadioButton.checkedBorderColour};
    }
    &:hover ~ ${TextBlock} {
        background-color: ${theme.inputRadioButton.hoverBackgroundColour};
    }
`;
